import { calendarDay, type CalendarDay, type WallClock } from '../validators/temporal.validator.js'
import { minutesOfDay, timeOfDay, type TimeOfDay } from '../validators/time-of-day.validator.js'

/** What placing a moment needs to know about its event. */
export interface TimelineEventDay {
  date: CalendarDay
  startsAt: WallClock | null
  endsAt: WallClock | null
}

/** The clock of a stored wall-clock timestamp. */
function clockOf(wall: WallClock): TimeOfDay {
  return timeOfDay(wall.slice(11, 16))
}

/**
 * The day a moment typed as a clock time belongs to.
 *
 * The event's own day, unless the event runs past midnight and the clock falls
 * in those small hours: no later than the event ends, and earlier than it
 * starts. A 16:00–01:00 wedding puts a 00:30 send-off on the following day and
 * a 15:00 family photo on its own. An event that ends before midnight has no
 * small hours, so every moment is on its day.
 *
 * This replaced "earlier than the last moment on the list means the next day",
 * which pushed anything typed out of order onto the following day: a 3pm photo
 * added after a 5pm ceremony, and a second 5pm moment added after that two days
 * on. The event's window is what says when its night ends, not the list.
 *
 * Seeding a template is different, and stays in `seedTimeline`: a template's
 * order is its running order, so a clock going backwards there does mean a new
 * day. See `docs/event-timeline.md`.
 */
export function timelineDay(time: TimeOfDay, event: TimelineEventDay): CalendarDay {
  const { date, startsAt, endsAt } = event
  if (startsAt === null || endsAt === null) return date
  const endDay = calendarDay(endsAt.slice(0, 10))
  if (endDay === date) return date
  const at = minutesOfDay(time)
  const inSmallHours = at <= minutesOfDay(clockOf(endsAt)) && at < minutesOfDay(clockOf(startsAt))
  return inSmallHours ? endDay : date
}
